import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Stethoscope, ClipboardList, Truck, Activity, AlertTriangle, MapIcon,
  Gauge, CheckCircle2, TrendingUp, RefreshCw, BrainCircuit, ChevronRight,
  Clock, Shield, Bell, Wrench, AlertOctagon, Sparkles, MapPin,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip as RTooltip, Legend,
} from "recharts";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { VISIT_STATUS_COLORS, timeAgo } from "@/pages/fleet/constants";

const REFRESH_MS = 30000;

/* ── KPI Tile ─────────────────────────────────────────────── */
function KpiTile({ icon: Icon, label, value, accent = "#0B7E8C", sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="hah-card p-4 flex items-center gap-3 transition-transform hover:-translate-y-[1px]"
    >
      <span
        className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${accent}1F`, color: accent }}
      >
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <div className="text-[26px] leading-none font-semibold text-[#1F2937]">
          {value ?? "—"}
        </div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mt-1 truncate">
          {label}
        </div>
        {sub && <div className="text-[11px] text-[#94A3B8] mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
}

/* ── Section Header ───────────────────────────────────────── */
function SectionHeader({ icon: Icon, title, color }) {
  return (
    <div className="flex items-center gap-3 mb-3 mt-2">
      <span
        className="w-5 h-5 rounded-md flex items-center justify-center"
        style={{ background: `${color}24`, color }}
      >
        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
      <h2
        className="text-[11px] font-bold uppercase tracking-[0.18em]"
        style={{ color }}
      >
        {title}
      </h2>
      <div className="h-px flex-1 bg-[#E5E7EB]" />
    </div>
  );
}

/* ── Live indicator ───────────────────────────────────────── */
function LivePill() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
    </span>
  );
}

/* ── Status pill (reuse VISIT_STATUS_COLORS but theme-aware) ── */
function StatusPill({ status }) {
  const c = VISIT_STATUS_COLORS[status] || VISIT_STATUS_COLORS.cancelled;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border"
      style={{ background: `${c.text}1A`, color: c.text, borderColor: `${c.text}40` }}
    >
      {c.label}
    </span>
  );
}

const DONUT_COLORS = {
  unassigned: "#F59E0B",
  assigned: "#3B82F6",
  en_route: "#8B5CF6",
  in_progress: "#A855F7",
  completed: "#10B981",
  delayed: "#EF4444",
};

/* ── Main ─────────────────────────────────────────────────── */
export default function FleetDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState({ kpis: { workforce: {}, visits: {}, vehicles: {}, performance: {} } });
  const [visits, setVisits] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [dRes, vRes, hRes] = await Promise.all([
        api.get("/fleet/dashboard"),
        api.get("/fleet/visits?page_size=50"),
        api.get("/fleet/vehicles"),
      ]);
      setData(dRes.data);
      setVisits(vRes.data?.items || []);
      setVehicles(hRes.data?.items || hRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const k = data.kpis;

  /* Donut data */
  const donut = useMemo(() => {
    const v = k.visits || {};
    const slices = [
      { key: "unassigned",  name: "Unassigned",  value: v.unassigned  || 0 },
      { key: "assigned",    name: "Assigned",    value: v.assigned    || 0 },
      { key: "en_route",    name: "En Route",    value: v.en_route    || 0 },
      { key: "in_progress", name: "In Progress", value: v.in_progress || 0 },
      { key: "completed",   name: "Completed",   value: v.completed   || 0 },
      { key: "delayed",     name: "Delayed",     value: v.delayed     || 0 },
    ];
    const total = slices.reduce((s, x) => s + x.value, 0);
    return { slices, total };
  }, [k.visits]);

  /* Upcoming visits = today's assigned/en_route/in_progress */
  const upcoming = useMemo(() => {
    return visits
      .filter((v) => ["assigned", "en_route", "in_progress", "accepted", "unassigned"].includes(v.status))
      .slice(0, 6);
  }, [visits]);

  /* 7-day trend from completed/created_at on visits */
  const trend = useMemo(() => {
    const today = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      days.push({ key, label, Completed: 0, "In Progress": 0, Cancelled: 0, Delayed: 0 });
    }
    const idx = Object.fromEntries(days.map((d, i) => [d.key, i]));
    for (const v of visits) {
      const ts = v.completed_at || v.scheduled_at || v.created_at;
      if (!ts) continue;
      const key = (typeof ts === "string" ? ts : "").slice(0, 10);
      const i = idx[key];
      if (i === undefined) continue;
      if (v.status === "completed") days[i].Completed += 1;
      else if (v.status === "in_progress" || v.status === "arrived") days[i]["In Progress"] += 1;
      else if (v.status === "cancelled" || v.status === "missed") days[i].Cancelled += 1;
      if (v.sla_breached && v.status !== "completed") days[i].Delayed += 1;
    }
    return days;
  }, [visits]);

  /* Vehicle status list (top 6) */
  const vehicleList = useMemo(() => vehicles.slice(0, 6), [vehicles]);

  /* Alerts list (derive from visits + vehicles) */
  const alerts = useMemo(() => {
    const a = [];
    vehicles.filter((v) => v.status === "maintenance").slice(0, 2).forEach((v) =>
      a.push({
        id: `veh-${v.id}`,
        icon: Wrench,
        color: "#F59E0B",
        title: `${v.plate_number || v.code || "Vehicle"} is under maintenance`,
        time: timeAgo(v.updated_at || v.created_at),
      })
    );
    visits.filter((v) => v.sla_breached && v.status !== "completed").slice(0, 3).forEach((v) =>
      a.push({
        id: `sla-${v.id}`,
        icon: AlertOctagon,
        color: "#EF4444",
        title: `SLA at risk for ${v.patient_name || "patient"}`,
        time: timeAgo(v.scheduled_at || v.updated_at),
      })
    );
    visits.filter((v) => v.status === "unassigned").slice(0, 2).forEach((v) =>
      a.push({
        id: `un-${v.id}`,
        icon: AlertTriangle,
        color: "#F59E0B",
        title: `Visit for ${v.patient_name || "patient"} is unassigned`,
        time: timeAgo(v.created_at),
      })
    );
    return a.slice(0, 6);
  }, [visits, vehicles]);

  const now = new Date();
  const dateLabel = now.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timeLabel = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="fleet-dashboard">
      <PageHeader
        title="Fleet Command Center"
        subtitle="Real-time operational view of clinicians, visits, and vehicles."
        actions={
          <div className="flex items-center gap-2">
            <LivePill />
            <Button
              onClick={() => navigate("/fleet/map")}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
              data-testid="fleet-open-map"
            >
              <MapIcon className="w-4 h-4 mr-1.5" /> Live Operations Map
            </Button>
            <Button
              variant="outline"
              onClick={load}
              className="border-[#E5E7EB]"
              data-testid="fleet-refresh"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <div className="hidden md:flex items-center gap-1 text-xs text-[#6B7280] px-2 py-1.5 rounded-md border border-[#E5E7EB]">
              <Clock className="w-3.5 h-3.5" /> {dateLabel} · {timeLabel}
            </div>
          </div>
        }
      />

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <>
          {/* WORKFORCE OVERVIEW */}
          <SectionHeader icon={Sparkles} title="Workforce Overview" color="#10B981" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <KpiTile icon={Users}        label="Total Clinicians" value={k.workforce.total}     accent="#0B7E8C"
                     sub={k.workforce.total ? `${Math.round((k.workforce.available / k.workforce.total) * 100)}% available` : null}
                     testId="fleet-kpi-total-clinicians" />
            <KpiTile icon={CheckCircle2} label="Available"        value={k.workforce.available} accent="#10B981"
                     sub={k.workforce.total ? `${Math.round((k.workforce.available / k.workforce.total) * 100)}% of total` : null}
                     testId="fleet-kpi-available" />
            <KpiTile icon={Stethoscope}  label="Ready"            value={k.workforce.ready}     accent="#3B82F6"
                     sub="Ready for dispatch" testId="fleet-kpi-ready" />
            <KpiTile icon={Activity}     label="Busy"             value={k.workforce.busy}      accent="#F59E0B"
                     sub="On active visit" testId="fleet-kpi-busy" />
            <KpiTile icon={Users}        label="Offline"          value={k.workforce.offline}   accent="#94A3B8"
                     sub={k.workforce.total ? `${Math.round((k.workforce.offline / k.workforce.total) * 100)}% of total` : null}
                     testId="fleet-kpi-offline" />
          </div>

          {/* VISITS OVERVIEW */}
          <SectionHeader icon={ClipboardList} title="Visits Overview" color="#8B5CF6" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <KpiTile icon={ClipboardList} label="Unassigned"  value={k.visits.unassigned}  accent="#F59E0B" testId="fleet-kpi-unassigned" />
            <KpiTile icon={ClipboardList} label="Assigned"    value={k.visits.assigned}    accent="#3B82F6" testId="fleet-kpi-assigned" />
            <KpiTile icon={MapPin}        label="En Route"    value={k.visits.en_route}    accent="#8B5CF6" sub="On the way" testId="fleet-kpi-en-route" />
            <KpiTile icon={Activity}      label="In Progress" value={k.visits.in_progress} accent="#A855F7" sub="Active visits" testId="fleet-kpi-in-progress" />
            <KpiTile icon={CheckCircle2}  label="Completed"   value={k.visits.completed}   accent="#10B981" sub="Today" testId="fleet-kpi-completed" />
            <KpiTile icon={AlertTriangle} label="Delayed"     value={k.visits.delayed}     accent="#EF4444" sub={k.visits.delayed ? "Needs review" : "On schedule"} testId="fleet-kpi-delayed" />
          </div>

          {/* FLEET OVERVIEW */}
          <SectionHeader icon={Truck} title="Fleet Overview" color="#06B6D4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiTile icon={Truck}         label="Total Vehicles" value={k.vehicles.total}       accent="#06B6D4" testId="fleet-kpi-veh-total" />
            <KpiTile icon={Truck}         label="Available"      value={k.vehicles.available}   accent="#10B981"
                     sub={k.vehicles.total ? `${Math.round((k.vehicles.available / k.vehicles.total) * 100)}% of total` : null} testId="fleet-kpi-veh-available" />
            <KpiTile icon={Truck}         label="Assigned"       value={k.vehicles.assigned}    accent="#3B82F6"
                     sub={k.vehicles.total ? `${Math.round((k.vehicles.assigned / k.vehicles.total) * 100)}% of total` : null} testId="fleet-kpi-veh-assigned" />
            <KpiTile icon={Wrench}        label="Maintenance"    value={k.vehicles.maintenance} accent="#F59E0B"
                     sub={k.vehicles.total ? `${Math.round((k.vehicles.maintenance / k.vehicles.total) * 100)}% of total` : null} testId="fleet-kpi-veh-maintenance" />
          </div>

          {/* OPS ROW: Map + Donut + Upcoming */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <MapPreviewCard onOpen={() => navigate("/fleet/map")} k={k} />
            <VisitsDonutCard donut={donut} onView={() => navigate("/fleet/visits")} />
            <UpcomingVisitsCard items={upcoming} onView={() => navigate("/fleet/visits")} />
          </div>

          {/* PERFORMANCE */}
          <SectionHeader icon={Gauge} title="Performance Metrics" color="#A855F7" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            <KpiTile icon={Activity}     label="Avg Travel Time"    value={`${k.performance.avg_travel_min ?? 0}min`}   accent="#6366F1" sub="vs yesterday" testId="fleet-kpi-avg-travel" />
            <KpiTile icon={Clock}        label="Avg Visit Duration" value={`${k.performance.avg_visit_min ?? 0}min`}    accent="#0B7E8C" sub="vs yesterday" testId="fleet-kpi-avg-visit" />
            <KpiTile icon={TrendingUp}   label="On-Time Performance"value={`${Math.round((1 - (k.visits?.delayed || 0) / Math.max(1, (k.visits?.completed || 0) + (k.visits?.in_progress || 0))) * 100)}%`} accent="#3B82F6" sub="last 24h" testId="fleet-kpi-ontime" />
            <KpiTile icon={Shield}       label="SLA Compliance"     value={`${k.performance.sla_compliance_pct ?? 0}%`} accent="#10B981" sub="vs yesterday" testId="fleet-kpi-sla" />
            <KpiTile icon={BrainCircuit} label="AI Route Acceptance"value={`${k.performance.ai_acceptance_pct ?? 0}%`}  accent="#A855F7"
                     sub={k.performance.ai_acceptance_pct ? "Last 7 days" : "No suggested routes today"} testId="fleet-kpi-ai" />
          </div>

          {/* BOTTOM ROW: Vehicles + Trends + Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <VehicleStatusCard items={vehicleList} onView={() => navigate("/fleet/vehicles")} />
            <VisitsTrendCard trend={trend} />
            <AlertsCard items={alerts} />
          </div>
        </>
      )}
    </div>
  );
}

/* ── Map preview card ────────────────────────────────────── */
function MapPreviewCard({ onOpen, k }) {
  return (
    <div className="hah-card p-5 flex flex-col" data-testid="fleet-map-preview">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Live Operations Map</div>
        <button onClick={onOpen} className="text-[#0B7E8C] text-xs font-medium hover:underline inline-flex items-center gap-1" data-testid="fleet-map-open">
          Open <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Stylized map placeholder using radial gradients */}
      <button
        onClick={onOpen}
        className="relative w-full aspect-[4/3] rounded-xl overflow-hidden border border-[#E5E7EB] group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B7E8C]/40"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, rgba(20,184,166,0.18), transparent 40%), " +
            "radial-gradient(circle at 65% 55%, rgba(59,130,246,0.18), transparent 38%), " +
            "radial-gradient(circle at 45% 75%, rgba(245,158,11,0.18), transparent 36%), " +
            "linear-gradient(180deg, rgba(11,126,140,0.06), rgba(11,126,140,0.02))",
        }}
      >
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-40"
             style={{ backgroundImage:
               "linear-gradient(to right, rgba(148,163,184,0.18) 1px, transparent 1px), " +
               "linear-gradient(to bottom, rgba(148,163,184,0.18) 1px, transparent 1px)",
               backgroundSize: "32px 32px" }} />
        {/* Markers */}
        {[
          { top: "26%", left: "22%", color: "#10B981", label: "🏠" },
          { top: "30%", left: "62%", color: "#3B82F6", label: "🏠" },
          { top: "55%", left: "32%", color: "#A855F7", label: "2" },
          { top: "55%", left: "50%", color: "#0B7E8C", label: "" },
          { top: "62%", left: "68%", color: "#3B82F6", label: "2" },
          { top: "70%", left: "44%", color: "#F59E0B", label: "1" },
        ].map((m, i) => (
          <span key={i} className="absolute w-8 h-8 rounded-full shadow-lg flex items-center justify-center text-xs text-white font-semibold"
                style={{ top: m.top, left: m.left, background: m.color, transform: "translate(-50%, -50%)" }}>
            {m.label || <MapPin className="w-4 h-4" />}
          </span>
        ))}
        <div className="absolute bottom-2 left-2 text-[10px] text-[#6B7280] bg-white/80 dark:bg-black/40 px-2 py-0.5 rounded">Riyadh</div>
      </button>
      {/* Counts strip */}
      <div className="grid grid-cols-4 gap-2 mt-3">
        {[
          { label: "Available",   value: k.vehicles?.available || 0,   color: "#10B981" },
          { label: "En Route",    value: k.visits?.en_route || 0,      color: "#3B82F6" },
          { label: "On Visit",    value: k.visits?.in_progress || 0,   color: "#A855F7" },
          { label: "Maintenance", value: k.vehicles?.maintenance || 0, color: "#F59E0B" },
        ].map((s) => (
          <div key={s.label} className="border border-[#E5E7EB] rounded-lg px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280]">{s.label}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Truck className="w-3.5 h-3.5" style={{ color: s.color }} />
              <span className="text-sm font-semibold text-[#1F2937]">{s.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Visits donut ────────────────────────────────────────── */
function VisitsDonutCard({ donut, onView }) {
  return (
    <div className="hah-card p-5" data-testid="fleet-visits-donut">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Today&apos;s Visits by Status</div>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative w-[170px] h-[170px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={donut.slices} dataKey="value" innerRadius={55} outerRadius={80} startAngle={90} endAngle={-270} strokeWidth={0}>
                {donut.slices.map((s) => (
                  <Cell key={s.key} fill={DONUT_COLORS[s.key]} />
                ))}
              </Pie>
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="text-2xl font-bold text-[#1F2937]">{donut.total}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Total Visits</div>
          </div>
        </div>
        <div className="flex-1 space-y-1.5">
          {donut.slices.map((s) => {
            const pct = donut.total ? Math.round((s.value / donut.total) * 100) : 0;
            return (
              <div key={s.key} className="flex items-center justify-between text-[12px]">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: DONUT_COLORS[s.key] }} />
                  <span className="text-[#374151]">{s.name}</span>
                </div>
                <div className="font-medium text-[#1F2937] tabular-nums">
                  {s.value} <span className="text-[#94A3B8]">({pct}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <button onClick={onView} className="mt-3 w-full text-center py-1.5 text-[12px] font-medium text-[#0B7E8C] hover:bg-[#EAF8FA] rounded-md inline-flex items-center justify-center gap-1" data-testid="fleet-view-all-visits">
        View All Visits <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ── Upcoming visits ─────────────────────────────────────── */
function UpcomingVisitsCard({ items, onView }) {
  return (
    <div className="hah-card p-5" data-testid="fleet-upcoming-visits">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Upcoming Visits</div>
        <button onClick={onView} className="text-[#0B7E8C] text-xs font-medium hover:underline inline-flex items-center gap-1">
          View All <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2.5">
        {items.length === 0 && (
          <div className="text-[12px] text-[#94A3B8] py-6 text-center">No upcoming visits.</div>
        )}
        {items.map((v) => {
          const time = v.scheduled_at ? new Date(v.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—";
          return (
            <div key={v.id} className="flex items-center justify-between gap-3 py-1">
              <div className="w-14 text-[12px] font-semibold text-[#1F2937] tabular-nums shrink-0">{time}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#1F2937] truncate">{v.patient_name || "—"}</div>
                <div className="text-[11px] text-[#6B7280] truncate">{v.patient_address || "Riyadh"}</div>
              </div>
              <StatusPill status={v.status} />
              <div className="text-[11px] text-[#6B7280] w-24 truncate text-right">{v.clinician_name || "—"}</div>
            </div>
          );
        })}
      </div>
      <button onClick={onView} className="mt-3 w-full text-center py-1.5 text-[12px] font-medium text-[#0B7E8C] hover:bg-[#EAF8FA] rounded-md inline-flex items-center justify-center gap-1">
        View Full Schedule <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ── Vehicle status ──────────────────────────────────────── */
function VehicleStatusCard({ items, onView }) {
  return (
    <div className="hah-card p-5" data-testid="fleet-vehicle-status">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Vehicle Status</div>
        <button onClick={onView} className="text-[#0B7E8C] text-xs font-medium hover:underline">View All</button>
      </div>
      <div className="space-y-2.5">
        {items.length === 0 && (
          <div className="text-[12px] text-[#94A3B8] py-6 text-center">No vehicles registered.</div>
        )}
        {items.map((v) => {
          const status = (v.status || "available").toLowerCase();
          const statusColor = {
            available: { bg: "#10B98122", text: "#10B981" },
            assigned: { bg: "#3B82F622", text: "#3B82F6" },
            en_route: { bg: "#8B5CF622", text: "#8B5CF6" },
            in_use: { bg: "#A855F722", text: "#A855F7" },
            maintenance: { bg: "#F59E0B22", text: "#F59E0B" },
          }[status] || { bg: "#94A3B822", text: "#94A3B8" };
          const fuel = v.fuel_pct ?? v.battery_pct ?? 85;
          const fuelColor = fuel > 50 ? "#10B981" : fuel > 25 ? "#F59E0B" : "#EF4444";
          return (
            <div key={v.id} className="flex items-center gap-3 py-1">
              <div className="w-9 h-9 rounded-md bg-[#F1F5F9] flex items-center justify-center shrink-0">
                <Truck className="w-4 h-4 text-[#475569]" />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-[#1F2937] truncate">
                  {v.plate_number || v.code || "—"}
                </div>
                <div className="text-[11px] text-[#6B7280] truncate">{v.model || v.make || "—"}</div>
              </div>
              <span
                className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase"
                style={{ background: statusColor.bg, color: statusColor.text }}
              >
                {status.replace("_", " ")}
              </span>
              <div className="text-[11px] text-[#6B7280] w-20 truncate text-right hidden md:block">{v.assigned_area || v.current_zone || "—"}</div>
              <div className="w-20 shrink-0">
                <div className="text-[10px] text-[#6B7280] text-right">{fuel}%</div>
                <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${fuel}%`, background: fuelColor }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Visits Trends line chart ────────────────────────────── */
function VisitsTrendCard({ trend }) {
  return (
    <div className="hah-card p-5" data-testid="fleet-visits-trend">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Visits Trends (Last 7 Days)</div>
      </div>
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <RTooltip />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="Completed"   stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="In Progress" stroke="#A855F7" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Cancelled"   stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="Delayed"     stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ── Alerts ─────────────────────────────────────────────── */
function AlertsCard({ items }) {
  return (
    <div className="hah-card p-5" data-testid="fleet-alerts">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">Alerts & Notifications</div>
        <Bell className="w-3.5 h-3.5 text-[#6B7280]" />
      </div>
      <div className="space-y-2.5">
        {items.length === 0 && (
          <div className="text-[12px] text-[#94A3B8] py-6 text-center">No active alerts.</div>
        )}
        {items.map((a) => {
          const Icon = a.icon;
          return (
            <div key={a.id} className="flex items-center gap-3 py-1">
              <span
                className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                style={{ background: `${a.color}1F`, color: a.color }}
              >
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="text-[12.5px] text-[#1F2937] flex-1 truncate">{a.title}</div>
              <div className="text-[11px] text-[#6B7280] shrink-0 tabular-nums">{a.time}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
